import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader, ClipboardCheck } from 'lucide-react';
import { supabase } from '../services/supabaseClient';

const AUDIT_STATUSES = [
  { value: 'pass', label: 'Pass' },
  { value: 'needs_improvement', label: 'Needs improvement' },
  { value: 'fail', label: 'Fail' },
];

const SCORE_OPTIONS = [5, 4, 3, 2, 1];

const CallAuditModal = ({ open, onClose, call, agentName, onSave, readOnly = false }) => {
  const [status, setStatus] = useState('');
  const [score, setScore] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('roadserve_current_user') || 'null');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!open || !call) return;
    setError(null);
    setSuccess(false);
    setStatus(call.audit_status || '');
    setScore(
      call.audit_score !== null && call.audit_score !== undefined && call.audit_score !== ''
        ? String(call.audit_score)
        : ''
    );
    setFeedback(call.audit_feedback || '');
  }, [open, call]);

  const handleSave = async () => {
    if (!call?.callid) return;
    if (readOnly) {
      onClose();
      return;
    }
    if (!status) {
      setError('Please select audit status');
      return;
    }
    const parsedScore =
      score === '' || score === null || score === undefined ? null : Number(score);
    if (parsedScore !== null && Number.isNaN(parsedScore)) {
      setError('Score must be a number');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        audit_status: status || null,
        audit_score: parsedScore,
        audit_feedback: feedback.trim() || null,
        audit_updated_at: new Date().toISOString(),
        audit_updated_by: currentUser?.id || null,
        audit_updated_by_name: currentUser?.name || currentUser?.email || null,
      };

      const { data, error: updateError } = await supabase
        .from('call_reports')
        .update(payload)
        .eq('callid', call.callid)
        .select();

      if (updateError) throw updateError;

      setSuccess(true);
      if (onSave) onSave(data?.[0] || null);

      setTimeout(() => {
        onClose();
      }, 700);
    } catch (e) {
      setError(e?.message || 'Failed to save audit');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[95vh] overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <ClipboardCheck size={20} className="flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-base md:text-xl font-bold truncate">{readOnly ? 'Audit Feedback' : 'Call Audit'}</h2>
              <p className="text-xs md:text-sm opacity-90 truncate">Call ID: {call?.callid || 'N/A'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors flex-shrink-0 ml-2"
            disabled={loading}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(95vh-150px)]">
          <div className="mb-4 p-3 bg-teal-50 border border-teal-100 rounded-lg text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0">
                <div className="text-xs text-gray-500">Customer</div>
                <div className="font-medium text-gray-900 truncate">{call?.cnumber || 'N/A'}</div>
              </div>
              <div className="min-w-0">
                <div className="text-xs text-gray-500">Agent</div>
                <div className="font-medium text-gray-900 truncate">{agentName || '—'}</div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-2">
              <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-700">Audit saved. Closing…</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audit Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              >
                <option value="">Select status…</option>
                {AUDIT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Score</label>
              <select
                value={score}
                onChange={(e) => setScore(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              >
                <option value="">—</option>
                {SCORE_OPTIONS.map((s) => (
                  <option key={s} value={String(s)}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Feedback</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              disabled={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Write audit notes / feedback…"
            />
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
              disabled={loading}
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-2"
                disabled={loading}
              >
                {loading && <Loader size={16} className="animate-spin" />}
                Save Audit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallAuditModal;


